import { useEffect, useRef, useState } from 'react';
import { Alert } from 'react-bootstrap';
import { Trans, useTranslation } from 'react-i18next';
import { NavLink, useNavigate, useSearchParams } from 'react-router-dom';
import Loader from '../../components/loader/Loader';
import useCompleteOnboarding from './useCompleteOnboarding';
import { Container } from './CompleteRegistration.styles';
import { SubscriptionOnboardingErrorCode } from './CompleteRegistration.types';

const errorCodeToMessageType: Partial<
  Record<SubscriptionOnboardingErrorCode, string>
> = {
  [SubscriptionOnboardingErrorCode.SUBSCRIPTION_ONBOARDING_ALREADY_COMPLETED]:
    'info',
};

function shouldErrorCodeRedirectToLoginPage(
  errorCode: SubscriptionOnboardingErrorCode | string | null,
) {
  return (
    errorCode &&
    Object.values(SubscriptionOnboardingErrorCode).includes(
      errorCode as SubscriptionOnboardingErrorCode,
    )
  );
}

const CompleteRegistration = () => {
  const [errorMessage, setErrorMessage] =
    useState<SubscriptionOnboardingErrorCode | null>(null);
  const { mutateAsync: completeOnboarding } = useCompleteOnboarding();
  const navigate = useNavigate();
  const hasRun = useRef<boolean>(false);
  const [searchParams] = useSearchParams();
  const { t } = useTranslation();

  const token = searchParams.get('t');

  useEffect(() => {
    if (token) {
      if (hasRun.current) {
        return;
      }

      hasRun.current = true;

      completeOnboarding({ token })
        .then(({ data: { externalRedirectUrl, userAccessToken } }) => {
          if (externalRedirectUrl) {
            window.location.href = externalRedirectUrl;
            return;
          }

          if (userAccessToken) {
            localStorage.setItem('access_token', userAccessToken);

            window.location.href = '/onboarding/agency/wizard/invite.jsp';
          }
        })
        .catch(
          ({
            response: {
              data: { apiErrorCode, apiErrorMessage },
            },
          }) => {
            setErrorMessage(apiErrorCode || apiErrorMessage);
          },
        );
    } else {
      setErrorMessage(
        SubscriptionOnboardingErrorCode.SUBSCRIPTION_ONBOARDING_INVALID_LINK,
      );
    }
  }, [token]);

  useEffect(() => {
    if (shouldErrorCodeRedirectToLoginPage(errorMessage)) {
      setTimeout(() => navigate('/login'), 10000);
    }
  }, [errorMessage]);

  if (errorMessage) {
    return (
      <Container>
        <Alert
          bsStyle={errorCodeToMessageType[errorMessage] ?? 'danger'}
          className="text-center"
          data-testid="error-message"
        >
          <Trans
            i18nKey="pageOnboarding.complete.error"
            components={{ 1: <NavLink to="/login" /> }}
            context={errorMessage}
          />
        </Alert>
      </Container>
    );
  }

  return (
    <Container>
      <Loader />
      <h3 className="text-center" data-testid="progress-info">
        {t('pageOnboarding.complete.progressInfo')}
      </h3>
    </Container>
  );
};

export default CompleteRegistration;
